import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import {
  ANALYTICS_EVENTS,
  trackAuthFailure,
  trackEvent,
  trackPageView,
} from '../utils/analytics';

type TabOption = 'profile' | 'password' | 'contributions';
type ContributionFilter = 'all' | 'support' | 'oppose';
type ContributionStatusFilter = 'all' | 'completed' | 'opened' | 'not_completed';
type ContributionSort = 'newest' | 'amount';

type ContributionRow = {
  id: string;
  amount: number;
  type: 'support' | 'oppose';
  created_at: string;
  comment: string | null;
  status: string | null;
  campaign: { id: string; title: string } | null;
  org: { id: string; name: string } | null;
  org_id: string | null;
  campaign_id: string | null;
};

const SITE_URL = 'https://ballotr.com';
const SUPPORT_EMAIL = 'support@ballotr.com';

const getStatusFilterMatch = (
  contributionStatus: string | null,
  statusFilter: ContributionStatusFilter
) => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'completed') return contributionStatus === 'self_reported_completed';
  if (statusFilter === 'opened') return contributionStatus === 'redirected';
  if (statusFilter === 'not_completed') return contributionStatus === 'canceled';
  return true;
};

const getStatusLabel = (status: string | null) => {
  if (status === 'self_reported_completed') return 'User reported completed';
  if (status === 'redirected') return 'Opened donation page';
  if (status === 'canceled') return 'Not completed';
  return 'Unknown';
};

const Profile: React.FC = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState<TabOption>('contributions');
  const [party, setParty] = useState('');
  const [allContributions, setAllContributions] = useState<ContributionRow[]>([]);
  const [contributions, setContributions] = useState<ContributionRow[]>([]);
  const [filter, setFilter] = useState<ContributionFilter>('all');
  const [statusFilter, setStatusFilter] = useState<ContributionStatusFilter>('all');
  const [orgFilterOrgId, setOrgFilterOrgId] = useState<string | null>(null);
  const [sort, setSort] = useState<ContributionSort>('newest');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const pageSize = 10;
  const profileViewedRef = useRef(false);

  useEffect(() => {
    if (profileViewedRef.current) return;

    trackEvent(ANALYTICS_EVENTS.PROFILE_VIEWED, {
      initial_tab: 'contributions',
    });

    profileViewedRef.current = true;
  }, []);

  useEffect(() => {
    trackPageView('profile');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('political_party')
        .eq('id', user.id)
        .single();

      if (!error && data) setParty(data.political_party);

      const { data: redirectRows, error: allErr } = await supabase
        .from('donation_redirect_events')
        .select(
          `
          id,
          amount_cents,
          intent_type,
          created_at,
          comment,
          status,
          campaign_id,
          org_id,
          campaign:campaign_id ( id, title ),
          org:org_id ( id, name )
        `
        )
        .eq('user_id', user.id);

      if (allErr) {
        console.error(allErr);
        toast.error(allErr.message || 'Failed to load contributions');
      }

      const orgNameById = new Map<string, string>();
      if (!allErr && redirectRows && redirectRows.length > 0) {
        const orgIds = Array.from(
          new Set(
            redirectRows
              .map((r: any) => r.org_id)
              .filter((x: any) => typeof x === 'string' && x.length > 0)
          )
        );

        if (orgIds.length > 0) {
          const { data: orgRows, error: orgErr } = await supabase
            .from('political_orgs')
            .select('id, name')
            .in('id', orgIds);

          if (orgErr) {
            console.error(orgErr);
            toast.error(orgErr.message || 'Failed to load organization names');
          } else {
            for (const o of orgRows || []) {
              if (o?.id && o?.name) orgNameById.set(o.id, o.name);
            }
          }
        }
      }

      if (!allErr && redirectRows) {
        const normalized: ContributionRow[] = redirectRows.map((r: any) => {
          const org0 = Array.isArray(r.org) ? r.org[0] : r.org ?? null;
          const camp0 = Array.isArray(r.campaign) ? r.campaign[0] : r.campaign ?? null;

          const orgId: string | null = r.org_id || org0?.id || null;
          const orgName: string =
            org0?.name || (orgId ? orgNameById.get(orgId) : undefined) || 'Unknown Org';

          const orgFilled = orgId ? { id: orgId, name: orgName } : null;

          return {
            id: r.id,
            amount: Math.round((r.amount_cents || 0) / 100),
            type: r.intent_type,
            created_at: r.created_at,
            comment: r.comment,
            status: r.status,
            campaign: camp0,
            org: orgFilled,
            org_id: r.org_id,
            campaign_id: r.campaign_id,
          };
        });

        setAllContributions(normalized);
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const organizationOptions = useMemo(() => {
    const byOrg = new Map<string, { org_id: string; org_name: string; totalAmount: number }>();

    for (const contribution of allContributions) {
      const orgId = contribution.org?.id;
      const orgName = contribution.org?.name || 'Unknown Org';
      if (!orgId) continue;

      const existing = byOrg.get(orgId) || {
        org_id: orgId,
        org_name: orgName,
        totalAmount: 0,
      };

      existing.totalAmount += contribution.amount;
      byOrg.set(orgId, existing);
    }

    return Array.from(byOrg.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [allContributions]);

  const filteredContributions = useMemo(() => {
    return allContributions
      .filter((c) => {
        const matchesType = filter === 'all' || c.type === filter;
        const matchesStatus = getStatusFilterMatch(c.status, statusFilter);
        const matchesOrg = !orgFilterOrgId || c.org?.id === orgFilterOrgId;

        return matchesType && matchesStatus && matchesOrg;
      })
      .sort((a, b) => {
        if (sort === 'newest') {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
        return b.amount - a.amount;
      });
  }, [allContributions, filter, statusFilter, orgFilterOrgId, sort]);

  useEffect(() => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize;
    setContributions(filteredContributions.slice(from, to));
  }, [page, filteredContributions]);

  const summaryTotalAmount = filteredContributions.reduce((sum, c) => sum + c.amount, 0);
  const summarySupportCount = filteredContributions.filter((c) => c.type === 'support').length;
  const summaryOpposeCount = filteredContributions.filter((c) => c.type === 'oppose').length;

  const summaryByOrganization = useMemo(() => {
    const byOrg = new Map<string, { org_id: string; org_name: string; totalAmount: number }>();

    for (const contribution of filteredContributions) {
      const orgId = contribution.org?.id;
      const orgName = contribution.org?.name || 'Unknown Org';
      if (!orgId) continue;

      const existing = byOrg.get(orgId) || {
        org_id: orgId,
        org_name: orgName,
        totalAmount: 0,
      };

      existing.totalAmount += contribution.amount;
      byOrg.set(orgId, existing);
    }

    return Array.from(byOrg.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [filteredContributions]);

  const totalPages = Math.ceil(filteredContributions.length / pageSize);

  const handleOpenCampaign = (campaignId: string | null) => {
    if (!campaignId) return;
    navigate(`/campaign/${campaignId}`);
  };

  const handleSaveProfile = async () => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ political_party: party })
        .eq('id', userId);

      if (error) throw error;

      trackEvent(ANALYTICS_EVENTS.PROFILE_PARTY_UPDATED, {
        political_party: party || null,
      });

      toast.success('Profile updated!');
    } catch (error: any) {
      toast.error(error.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccountDeletion = async () => {
    const { data } = await supabase.auth.getUser();
    const userEmail = data?.user?.email || '';

    const subject = encodeURIComponent('Ballotr Account Deletion Request');
    const body = encodeURIComponent(
      `Please delete my Ballotr account and associated personal data.\n\nAccount email: ${userEmail}\n\nAdditional notes:`
    );

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGE_ATTEMPTED, {
      has_current_password: Boolean(currentPassword),
      has_new_password: Boolean(newPassword),
      has_confirm_password: Boolean(confirmPassword),
    });

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const userEmail = sessionData?.session?.user?.email;

    if (!userEmail || sessionError) {
      trackAuthFailure({
        flow: 'password_change',
        location: 'Profile.tsx',
        error: sessionError || new Error('No user session found.'),
        extraProperties: {
          reason: 'missing_session',
        },
      });

      toast.error('No user session found.');
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGE_FAILED, {
        reason: 'missing_fields',
      });

      toast.error('Please fill out all password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGE_FAILED, {
        reason: 'password_mismatch',
      });

      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: currentPassword,
      });

      if (signInError) {
        console.error('Re-auth failed:', signInError);
        throw signInError;
      }

      const { error: pwError } = await supabase.auth.updateUser({ password: newPassword });

      if (pwError) throw pwError;

      await supabase.auth.signOut({ scope: 'others' });

      trackEvent(ANALYTICS_EVENTS.PASSWORD_CHANGE_SUCCEEDED, {});

      toast.success('Password updated!');

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Password update failed:', error);

      trackAuthFailure({
        flow: 'password_change',
        location: 'Profile.tsx',
        error,
        extraProperties: {
          reason: 'request_failed',
        },
      });

      toast.error(error.message || 'Password update failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="p-4">Loading...</p>;

  return (
    <>
      <Helmet>
        <title>Profile | Ballotr</title>
        <meta
          name="description"
          content="Manage your Ballotr profile, password, and contribution activity."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/profile`} />
      </Helmet>

      <div className="max-w-md mx-auto p-6 bg-white rounded shadow mt-10">
        <div className="flex mb-6 border-b">
          {(['contributions', 'profile', 'password'] as TabOption[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`mr-4 px-4 py-2 text-sm font-medium ${
                tab === t ? 'border-b-2 border-primary text-primary' : 'text-gray-600'
              }`}
            >
              {t === 'profile'
                ? 'Edit Profile'
                : t === 'password'
                  ? 'Change Password'
                  : 'Contribution Activity'}
            </button>
          ))}
        </div>

        {tab === 'profile' && (
          <>
            <label className="block mb-2 text-sm text-textSecondary">Political Party</label>
            <select
              value={party}
              onChange={(e) => setParty(e.target.value)}
              className="w-full border px-4 py-2 rounded-md mb-4"
            >
              <option value="">Select Political Party</option>
              <option value="Democratic">Democratic</option>
              <option value="Republican">Republican</option>
              <option value="Green">Green</option>
              <option value="Libertarian">Libertarian</option>
              <option value="Independent">Independent</option>
              <option value="Other">Other</option>
            </select>

            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-ballotr-blue-light transition disabled:opacity-50"
            >
              Save Changes
            </button>

            <div className="mt-8 border-t pt-6">
              <h3 className="text-base font-semibold text-gray-900">Account management</h3>

              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
                <h4 className="font-semibold text-red-800">Delete your account</h4>

                <p className="mt-2 text-sm leading-relaxed text-red-900">
                  You can request deletion of your Ballotr account and associated personal data.
                  We may retain limited records if required for legal, security, fraud prevention,
                  or compliance reasons.
                </p>

                <button
                  type="button"
                  onClick={handleRequestAccountDeletion}
                  className="mt-4 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                >
                  Request account deletion
                </button>
              </div>
            </div>
          </>
        )}

        {tab === 'password' && (
          <form onSubmit={handleChangePassword}>
            <label className="block mb-1 text-sm text-textSecondary">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full border px-4 py-2 rounded-md mb-4"
              placeholder="Enter current password"
              autoComplete="current-password"
            />

            <label className="block mb-1 text-sm text-textSecondary">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border px-4 py-2 rounded-md mb-4"
              placeholder="Enter new password"
              autoComplete="new-password"
            />

            <label className="block mb-1 text-sm text-textSecondary">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border px-4 py-2 rounded-md mb-6"
              placeholder="Confirm new password"
              autoComplete="new-password"
            />

            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-ballotr-blue-light transition disabled:opacity-50"
            >
              Update Password
            </button>
          </form>
        )}

        {tab === 'contributions' && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 text-center mb-4">
              View your contribution activity across campaigns and organizations.
            </p>

            <div className="sticky top-0 z-10 bg-white pb-3 pt-2 border-b space-y-4">
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">Type</div>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'support', 'oppose'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => {
                        trackEvent(ANALYTICS_EVENTS.CONTRIBUTION_TAB_FILTER_USED, {
                          filter: f,
                        });
                        setPage(1);
                        setFilter(f);
                      }}
                      className={`px-3 py-1 rounded ${
                        filter === f ? 'bg-primary text-white' : 'bg-gray-200 text-gray-800'
                      }`}
                    >
                      {f[0].toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Status</div>
                  <div className="flex flex-wrap gap-2">
                    {([
                      { value: 'all', label: 'All' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'opened', label: 'Opened' },
                      { value: 'not_completed', label: 'Not Completed' },
                    ] as const).map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setPage(1);
                          setStatusFilter(option.value);
                        }}
                        className={`px-3 py-1 rounded ${
                          statusFilter === option.value
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-800'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center">
                  <select
                    value={sort}
                    onChange={(e) => {
                      const nextSort = e.target.value as ContributionSort;

                      trackEvent(ANALYTICS_EVENTS.PROFILE_SORT_CHANGED, {
                        previous_sort: sort,
                        next_sort: nextSort,
                      });

                      setPage(1);
                      setSort(nextSort);
                    }}
                    className="border rounded px-3 py-2 text-sm bg-white"
                  >
                    <option value="newest">Newest</option>
                    <option value="amount">Amount</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded text-sm border space-y-3">
              <div className="font-semibold text-gray-900">Summary</div>

              <div>
                <strong>Total Intended Amount:</strong> ${summaryTotalAmount}
              </div>

              <div>
                Support: {summarySupportCount} | Oppose: {summaryOpposeCount}
              </div>

              <div className="pt-1">
                <div className="font-semibold mb-1">By Organization:</div>
                {summaryByOrganization.length === 0 ? (
                  <div className="text-gray-600">No organizations match your current filters.</div>
                ) : (
                  <ul className="space-y-1">
                    {summaryByOrganization.map((org) => (
                      <li key={org.org_id} className="flex justify-between">
                        <span>{org.org_name}</span>
                        <span>${org.totalAmount}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="pt-1 text-xs text-gray-600 leading-relaxed">
                <div>
                  Includes organization donation pages opened and user-reported completed donations.
                </div>
                <div>Ballotr does not verify off-platform donations.</div>
              </div>
            </div>

            <div className="text-sm text-gray-700">
              <label className="font-semibold mr-2">Filter by Organization:</label>
              <select
                value={orgFilterOrgId || ''}
                onChange={(e) => {
                  const nextOrgId = e.target.value || null;

                  trackEvent(ANALYTICS_EVENTS.ORG_FILTER_USED, {
                    org_id: nextOrgId,
                  });

                  setPage(1);
                  setOrgFilterOrgId(nextOrgId);
                }}
                className="border px-3 py-1 rounded"
              >
                <option value="">All Organizations</option>
                {organizationOptions.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.org_name} (${org.totalAmount})
                  </option>
                ))}
              </select>
            </div>

            {contributions.length === 0 ? (
              <p>No contribution activity matches your filters.</p>
            ) : (
              contributions.map((c) =>
                c.campaign_id ? (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleOpenCampaign(c.campaign_id)}
                    className="w-full rounded-md border border-gray-300 bg-white p-4 text-left shadow-sm transition hover:border-primary hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary active:scale-[0.99] active:bg-gray-50 active:shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-textPrimary">
                          <strong>{c.type.toUpperCase()}</strong>{' '}
                          <span className="text-primary">{c.campaign?.title || 'a campaign'}</span>
                        </div>
                        <div>To: {c.org?.name || 'Unknown Org'}</div>
                        <div>Amount: ${c.amount}</div>
                        <div>Status: {getStatusLabel(c.status)}</div>
                        <div className="text-sm text-gray-600">
                          {new Date(c.created_at).toLocaleString()}
                        </div>
                        {c.comment && <div className="mt-2 text-sm">Comment: {c.comment}</div>}
                      </div>

                      <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-500" />
                    </div>
                  </button>
                ) : (
                  <div
                    key={c.id}
                    className="w-full rounded-md border border-gray-200 bg-white p-4 text-left shadow-sm cursor-default"
                  >
                    <div>
                      <strong>{c.type.toUpperCase()}</strong> {c.campaign?.title || 'a campaign'}
                    </div>
                    <div>To: {c.org?.name || 'Unknown Org'}</div>
                    <div>Amount: ${c.amount}</div>
                    <div>Status: {getStatusLabel(c.status)}</div>
                    <div className="text-sm text-gray-600">
                      {new Date(c.created_at).toLocaleString()}
                    </div>
                    {c.comment && <div className="mt-2 text-sm">Comment: {c.comment}</div>}
                  </div>
                )
              )
            )}

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      const nextPage = i + 1;

                      trackEvent(ANALYTICS_EVENTS.PROFILE_PAGINATION_USED, {
                        page: nextPage,
                        total_pages: totalPages,
                      });

                      setPage(nextPage);
                    }}
                    className={`px-3 py-1 rounded border ${
                      page === i + 1 ? 'bg-primary text-white' : 'text-gray-700'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;